/**
 * vmx-nav-drawer.js
 * Defines the <vmx-nav-drawer> Web Component.
 *
 * Security: NEVER uses innerHTML for user-controlled content.
 * All text inserted via textContent or setAttribute.
 * Markup cloned from <template id="vmx-nav-drawer-tmpl"> via cloneNode(true).
 *
 * Events consumed:
 *   vmx:drawer:open  { detail: { drawer: 'nav' } } — opens the nav drawer
 *
 * Events dispatched:
 *   vmx:drawer:close                               — fired when drawer closes
 */

(function () {
  'use strict';

  var TEMPLATE_ID = 'vmx-nav-drawer-tmpl';
  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  class VmxNavDrawer extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._nodes = null; // { backdrop, panel, closeBtn, ... }
      this._cleanupTimer = null; // pending close-cleanup, cancelled on re-open
      this._focusTrap = null;
      this._previousFocus = null;
      this._handleKeyDown = this._handleKeyDown.bind(this);
      this._handleOpen = this._handleOpen.bind(this);
    }

    connectedCallback() {
      document.addEventListener('vmx:drawer:open', this._handleOpen);
    }

    disconnectedCallback() {
      document.removeEventListener('vmx:drawer:open', this._handleOpen);
      this._unbindKeyDown();
    }

    _handleOpen(e) {
      if (!e.detail || e.detail.drawer !== 'nav') return;
      // Toggle: a second open request (e.g. tapping the hamburger again) closes it.
      if (this._open) { this._closeDrawer(); return; }
      this._mount();
      // If the template was missing, _mount() bailed and left _nodes null — don't
      // scroll-lock the page behind an invisible, unclosable drawer.
      if (!this._nodes) return;
      this._openDrawer();
    }

    /* ── Mount: clone template into DOM ─────────────────────────────── */
    _mount() {
      if (this._nodes) return; // already mounted

      var tmpl = document.getElementById(TEMPLATE_ID);
      if (!tmpl) {
        console.error('[vmx-nav-drawer] Template #' + TEMPLATE_ID + ' not found.');
        return;
      }

      var fragment = tmpl.content.cloneNode(true);
      this.appendChild(fragment);

      this._nodes = {
        backdrop:     this.querySelector('[data-vmx-nav-backdrop]'),
        panel:        this.querySelector('.vmx-nav-drawer'),
        closeBtn:     this.querySelector('[data-vmx-nav-close]'),
        brand:        this.querySelector('[data-vmx-nav-brand]'),
        trustText:    this.querySelector('[data-vmx-nav-trust-text]'),
        sectionsSlot: this.querySelector('[data-vmx-nav-sections]'),
        quickLinks:   this.querySelector('[data-vmx-nav-quick-links]'),
        social:       this.querySelector('[data-vmx-nav-social]'),
      };

      this._populateContent();
      this._bindClose();

      // The trust strip's [data-vmx-review-*] nodes were just cloned into the DOM,
      // after vmx-review-counts.js already ran its first paint. Ask it to repaint
      // so the rating + count fill in instead of staying on the "…" placeholder.
      document.dispatchEvent(new CustomEvent('vmx:reviews:repaint'));
    }

    /* ── Populate content from window.VMX_NAV_SECTIONS ──────────────── */
    _populateContent() {
      var cfg = window.VMX_NAV_SECTIONS;
      if (!cfg) return;

      // Brand name
      if (this._nodes.brand && cfg.brandName) {
        this._nodes.brand.textContent = cfg.brandName;
      }

      // Trust strip is data-attribute-driven (see vmx-review-counts.js) — that
      // script reads the canonical reviews JSON and updates these elements
      // in place. The hardcoded VMX_NAV_SECTIONS.reviews values are ignored.

      // Primary nav sections (accordions)
      if (this._nodes.sectionsSlot && Array.isArray(cfg.primaryNav)) {
        cfg.primaryNav.forEach(function (section, si) {
          var div = document.createElement('div');
          div.className = 'vmx-nav-drawer__section';
          div.style.animation = 'vmx-nav-item-in 280ms var(--vmx-ease-out) ' + (60 + (si + 1) * 48) + 'ms both';

          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'vmx-nav-drawer__section-trigger';
          btn.setAttribute('aria-expanded', 'false');
          btn.setAttribute('aria-controls', 'vmx-nav-section-links-' + si);

          var labelSpan = document.createElement('span');
          labelSpan.textContent = section.label;
          btn.appendChild(labelSpan);

          var chevron = document.createElement('span');
          chevron.className = 'vmx-nav-drawer__section-chevron';
          chevron.setAttribute('aria-hidden', 'true');
          // Safe SVG as static element (no user data)
          var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('width', '15');
          svg.setAttribute('height', '15');
          svg.setAttribute('viewBox', '0 0 15 15');
          svg.setAttribute('fill', 'none');
          var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', 'M5.5 3.5l4 4-4 4');
          path.setAttribute('stroke', 'currentColor');
          path.setAttribute('stroke-width', '1.6');
          path.setAttribute('stroke-linecap', 'round');
          path.setAttribute('stroke-linejoin', 'round');
          svg.appendChild(path);
          chevron.appendChild(svg);
          btn.appendChild(chevron);

          var linksDiv = document.createElement('div');
          linksDiv.className = 'vmx-nav-drawer__section-links';
          linksDiv.id = 'vmx-nav-section-links-' + si;
          // Collapsed by default: keep the hidden links out of the tab order /
          // a11y tree (they're only visually clipped via max-height:0 in CSS).
          linksDiv.setAttribute('inert', '');

          if (Array.isArray(section.links)) {
            section.links.forEach(function (link) {
              var a = document.createElement('a');
              a.href = link.href;
              a.className = 'vmx-nav-drawer__section-link';
              a.addEventListener('click', function () { this._closeDrawer(); }.bind(this));

              var dot = document.createElement('span');
              dot.className = 'vmx-nav-drawer__section-dot';
              dot.setAttribute('aria-hidden', 'true');
              a.appendChild(dot);

              var text = document.createElement('span');
              text.textContent = link.label;
              a.appendChild(text);
              linksDiv.appendChild(a);
            }, this);
          }

          // Accordion toggle. Use the real rendered height (scrollHeight) rather
          // than a fixed per-row guess, so long product/bundle names that wrap to
          // two lines aren't clipped.
          btn.addEventListener('click', function () {
            var expanded = btn.getAttribute('aria-expanded') === 'true';
            btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
            linksDiv.style.maxHeight = expanded ? '0' : linksDiv.scrollHeight + 'px';
            if (expanded) { linksDiv.setAttribute('inert', ''); }
            else { linksDiv.removeAttribute('inert'); }
          });

          div.appendChild(btn);
          div.appendChild(linksDiv);
          this._nodes.sectionsSlot.appendChild(div);
        }, this);
      }

      // Quick links below quiz card
      if (this._nodes.quickLinks && Array.isArray(cfg.quickLinks)) {
        cfg.quickLinks.forEach(function (link) {
          var a = document.createElement('a');
          a.href = link.href;
          a.className = 'vmx-nav-drawer__row';
          a.addEventListener('click', function () { this._closeDrawer(); }.bind(this));
          var label = document.createElement('span');
          label.className = 'vmx-nav-drawer__row-label';
          label.textContent = link.label;
          a.appendChild(label);
          this._nodes.quickLinks.appendChild(a);
        }, this);
      }

      // Social links in footer
      if (this._nodes.social && Array.isArray(cfg.socialLinks)) {
        cfg.socialLinks.forEach(function (s) {
          var a = document.createElement('a');
          a.href = s.href;
          a.className = 'vmx-nav-drawer__social-link';
          a.setAttribute('aria-label', s.label);
          if (s.href.startsWith('http')) {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
          }
          // Platform icon (SVG paths as static code — no user data)
          var icon = this._makeSocialIcon(s.platform);
          if (icon) a.appendChild(icon);
          this._nodes.social.appendChild(a);
        }, this);
      }
    }

    _makeSocialIcon(platform) {
      var paths = {
        instagram: 'M9 2H5a3 3 0 00-3 3v8a3 3 0 003 3h8a3 3 0 003-3V7M9 9a2 2 0 11-4 0 2 2 0 014 0zM14 2h.01',
        tiktok:    'M14 2v6.5A5.5 5.5 0 118.5 14M14 2a4 4 0 004 4',
        facebook:  'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z',
      };
      var d = paths[platform];
      if (!d) return null;
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '18');
      svg.setAttribute('height', '18');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('aria-hidden', 'true');
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', d);
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.6');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      svg.appendChild(path);
      return svg;
    }

    /* ── Bind close events ───────────────────────────────────────────── */
    _bindClose() {
      var self = this;

      if (this._nodes.backdrop) {
        this._nodes.backdrop.addEventListener('click', function () {
          self._closeDrawer();
        });
      }

      // All close buttons (header close + quiz CTA uses same attribute)
      var closeBtns = this.querySelectorAll('[data-vmx-nav-close]');
      closeBtns.forEach(function (btn) {
        btn.addEventListener('click', function () { self._closeDrawer(); });
      });
    }

    /* ── Open ─────────────────────────────────────────────────────────── */
    _openDrawer() {
      // A re-open that lands while a previous close is still animating out must
      // cancel that close's pending cleanup, or it would yank the panel we're
      // about to show back out of the DOM.
      if (this._cleanupTimer) { clearTimeout(this._cleanupTimer); this._cleanupTimer = null; }
      this._open = true;
      this._previousFocus = document.activeElement;
      document.body.style.overflow = 'hidden';
      this._bindKeyDown();

      var panel = this._nodes && this._nodes.panel;
      var backdrop = this._nodes && this._nodes.backdrop;
      // Commit the closed (translateX(-100%)) base state with a forced reflow so
      // the open transition reliably runs from it. Without this, a freshly
      // appended node + same-tick class add can skip the transition entirely.
      if (panel) void panel.offsetWidth;
      // Toggle the open class on the next frame so the transition fires from the
      // base state rather than jumping straight to open.
      requestAnimationFrame(function () {
        if (panel) panel.classList.add('is-open');
        if (backdrop) backdrop.classList.add('is-open');
        var first = this.querySelector(FOCUSABLE);
        if (first) first.focus();
      }.bind(this));
    }

    /* ── Close ────────────────────────────────────────────────────────── */
    _closeDrawer() {
      if (!this._open) return;
      this._open = false;
      document.body.style.overflow = '';
      this._unbindKeyDown();

      // Animate out then remove
      var panel = this._nodes && this._nodes.panel;
      var backdrop = this._nodes && this._nodes.backdrop;

      var self = this;
      function cleanup() {
        self._cleanupTimer = null;
        self._nodes = null;
        while (self.firstChild) self.removeChild(self.firstChild);
        document.dispatchEvent(new CustomEvent('vmx:drawer:close'));
      }

      if (panel) {
        // Transition back out by removing the open class, then remove the node
        // once the slide-out finishes.
        panel.classList.remove('is-open');
        if (backdrop) backdrop.classList.remove('is-open');
        this._cleanupTimer = setTimeout(cleanup, 400);
      } else {
        cleanup();
      }

      if (this._previousFocus) {
        this._previousFocus.focus();
        this._previousFocus = null;
      }
    }

    /* ── Focus trap ───────────────────────────────────────────────────── */
    _bindKeyDown() {
      document.addEventListener('keydown', this._handleKeyDown);
    }

    _unbindKeyDown() {
      document.removeEventListener('keydown', this._handleKeyDown);
    }

    _handleKeyDown(e) {
      if (e.key === 'Escape') {
        this._closeDrawer();
        return;
      }
      if (e.key === 'Tab') {
        var focusable = Array.from(this.querySelectorAll(FOCUSABLE));
        if (focusable.length === 0) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }
  }

  customElements.define('vmx-nav-drawer', VmxNavDrawer);
})();
